import React from 'react';
import { ChevronDown, ChevronRight, ClipboardCheck, MessagesSquare, Mic, Plus } from 'lucide-react';

import { adminContent } from 'services/api';
import type {
  AdminCatalogPage,
  ModuleLibraryRow,
  QuestionLibraryCatalogCourse,
  QuestionLibraryCourseCatalog,
  QuestionLibraryCourseTree,
} from 'services/api/adminContentClient';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { LoadingAnimation } from 'components/ui/loading-animation';
import { cn } from 'lib/utils';

import { courseMetaLine, groupCoursesForBuilders } from '../courseGrouping';
import CourseJumpLinks from '../CourseJumpLinks';
import { type BrowserCourseId, UNASSIGNED_COURSE } from '../QuestionLibrary/navigatorUtils';
import { moduleDisplayName } from './utils';

// Safety cap for the marker requests (10,000 modules).
const MAX_FLAG_PAGES = 100;

type ModuleFlags = Pick<ModuleLibraryRow, 'is_quiz' | 'is_roleplay' | 'voice_mode_enabled'>;

export type ModuleCourseBrowserProps = {
  catalog: QuestionLibraryCourseCatalog | null;
  catalogError?: string | null;
  courseId: BrowserCourseId | null;
  selectedFlowId: number | null;
  refreshToken: number;
  /** Called once the catalog loads while the URL names no course (Life Plan, or the server's fallback). */
  onDefaultCourse: (courseId: number) => void;
  onPickCourse: (courseId: BrowserCourseId) => void;
  onSelectModule: (flowId: number) => void;
  onCreateModule: () => void;
};

const CourseRow: React.FC<{
  course: QuestionLibraryCatalogCourse;
  active: boolean;
  onPick: () => void;
}> = ({ course, active, onPick }) => (
  <button
    type="button"
    onClick={onPick}
    data-testid={`fb-course-option-${course.id}`}
    className={cn(
      'flex w-full items-center gap-3 rounded-xl px-2.5 py-1.5 text-left text-sm hover:bg-foundation-calm-blue-50',
      active && 'bg-foundation-calm-blue-50 font-semibold',
    )}
  >
    <span
      aria-hidden="true"
      className={cn(
        'h-2.5 w-2.5 shrink-0 rounded-[3px]',
        course.looks_like_test ? 'bg-foundation-base-darkbase-dark-100' : 'bg-foundation-orange-500',
      )}
    />
    <span className="min-w-0 flex-1">
      <span className="block truncate">{course.name}</span>
      <span className="block truncate text-xs font-normal text-muted-foreground">{courseMetaLine(course)}</span>
    </span>
  </button>
);

const GroupLabel: React.FC<{ label: string; count: number }> = ({ label, count }) => (
  <p className="px-2.5 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
    {label} <span className="font-normal">{count}</span>
  </p>
);

/** Module Builder's By course tab: a course picker and that course's modules in learner order, by chapter. */
export const ModuleCourseBrowser: React.FC<ModuleCourseBrowserProps> = ({
  catalog,
  catalogError,
  courseId,
  selectedFlowId,
  refreshToken,
  onDefaultCourse,
  onPickCourse,
  onSelectModule,
  onCreateModule,
}) => {
  const [tree, setTree] = React.useState<QuestionLibraryCourseTree | null>(null);
  const [treeLoading, setTreeLoading] = React.useState(false);
  const [treeError, setTreeError] = React.useState<string | null>(null);
  const [flags, setFlags] = React.useState<Map<number, ModuleFlags>>(new Map());
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [courseSearch, setCourseSearch] = React.useState('');
  const [showTests, setShowTests] = React.useState(false);
  const pickerRef = React.useRef<HTMLDivElement>(null);
  const pickerButtonRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    if (courseId !== null || !catalog) {
      return;
    }
    const grouped = groupCoursesForBuilders(catalog.courses);
    const fallback = catalog.default_course_id ?? grouped.assigned[0]?.id ?? grouped.other[0]?.id ?? null;
    if (fallback !== null) {
      onDefaultCourse(fallback);
    }
  }, [catalog, courseId, onDefaultCourse]);

  React.useEffect(() => {
    if (courseId === null) {
      return undefined;
    }
    let cancelled = false;
    setTreeLoading(true);
    adminContent.questions
      // Module Builder is the admin catalog: inactive modules stay visible (marked Inactive).
      .courseTree(courseId, { include_inactive: true })
      .then((response) => {
        if (cancelled) return;
        setTree(response.data as QuestionLibraryCourseTree);
        setTreeError(null);
      })
      .catch(() => {
        if (cancelled) return;
        setTree(null);
        setTreeError('Could not load this course.');
      })
      .finally(() => {
        if (!cancelled) setTreeLoading(false);
      });
    // Quiz, roleplay and voice markers come from the module search, which already knows each module's type.
    // Fetch every page (100 at a time) so large lists, like the unassigned bucket, keep their markers.
    const loadFlags = async () => {
      const rows: ModuleLibraryRow[] = [];
      for (let page = 1; page <= MAX_FLAG_PAGES; page += 1) {
        // eslint-disable-next-line no-await-in-loop
        const response = await adminContent.flows.search({ course_id: courseId, include_inactive: true, page, page_size: 100 });
        const data = response.data as AdminCatalogPage<ModuleLibraryRow> | undefined;
        rows.push(...(data?.results ?? []));
        if (!data?.next) break;
      }
      return rows;
    };
    loadFlags()
      .then((rows) => {
        if (!cancelled) setFlags(new Map(rows.map((row) => [row.id, row])));
      })
      .catch(() => {
        if (!cancelled) setFlags(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, [courseId, refreshToken]);

  React.useEffect(() => {
    if (!pickerOpen) {
      return undefined;
    }
    const handlePointer = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setPickerOpen(false);
      }
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPickerOpen(false);
        pickerButtonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [pickerOpen]);

  const grouped = React.useMemo(() => groupCoursesForBuilders(catalog?.courses ?? []), [catalog]);
  const unassignedCount = (catalog?.unassigned_module_count ?? 0) + (catalog?.unassigned_inactive_module_count ?? 0);
  const activeCourse =
    typeof courseId === 'number' ? catalog?.courses.find((course) => course.id === courseId) : undefined;
  const term = courseSearch.trim().toLowerCase();
  const matches = (course: QuestionLibraryCatalogCourse) => !term || course.name.toLowerCase().includes(term);
  const assigned = grouped.assigned.filter(matches);
  const other = grouped.other.filter(matches);
  const tests = grouped.tests.filter(matches);
  const unassignedMatches = !term || 'modules not in any course'.includes(term);

  const pickCourse = (next: BrowserCourseId) => {
    setPickerOpen(false);
    setCourseSearch('');
    onPickCourse(next);
  };

  const pickerLabel =
    courseId === UNASSIGNED_COURSE ? 'Modules not in any course' : activeCourse?.name ?? tree?.course?.name ?? 'Choose a course';
  const pickerMeta =
    courseId === UNASSIGNED_COURSE
      ? `${unassignedCount} module${unassignedCount === 1 ? '' : 's'}`
      : activeCourse
        ? courseMetaLine(activeCourse)
        : '';

  // Only show the tree once it belongs to the course in the URL.
  const visibleTree =
    tree && (courseId === UNASSIGNED_COURSE ? tree.course === null : tree.course?.id === courseId) ? tree : null;
  const chapterTitle = new Map((visibleTree?.chapters ?? []).map((chapter) => [chapter.id, chapter.title]));
  const hasChapters = (visibleTree?.chapters.length ?? 0) > 0;
  const groups: Array<{ key: string; label: string | null; modules: Array<{ module: QuestionLibraryCourseTree['modules'][number]; number: number }> }> = [];
  (visibleTree?.modules ?? []).forEach((module, index) => {
    const key = `chapter-${module.chapter_id ?? 'none'}`;
    const last = groups[groups.length - 1];
    if (!last || last.key !== key) {
      groups.push({
        key,
        label: hasChapters ? (module.chapter_id ? chapterTitle.get(module.chapter_id) ?? 'Chapter' : 'Not in a chapter') : null,
        modules: [],
      });
    }
    groups[groups.length - 1].modules.push({ module, number: index + 1 });
  });

  return (
    <div
      className="flex max-h-[36rem] min-h-0 min-w-0 flex-col rounded-3xl bg-white/40 p-3 xl:sticky xl:top-4 xl:max-h-[calc(100vh-2rem)]"
      data-testid="fb-course-browser"
    >
      <div className="relative min-w-0" ref={pickerRef}>
        <button
          type="button"
          ref={pickerButtonRef}
          onClick={() => setPickerOpen((open) => !open)}
          aria-expanded={pickerOpen}
          aria-haspopup="listbox"
          data-testid="fb-course-picker"
          className="flex w-full items-center gap-3 rounded-2xl bg-white px-3 py-2.5 text-left shadow-sm"
        >
          <span
            aria-hidden="true"
            className={cn(
              'grid h-8 w-8 shrink-0 place-items-center rounded-xl text-sm font-bold text-white',
              activeCourse?.looks_like_test || courseId === UNASSIGNED_COURSE
                ? 'bg-foundation-base-darkbase-dark-300'
                : 'bg-foundation-orange-500',
            )}
          >
            {courseId === UNASSIGNED_COURSE ? '?' : (pickerLabel.trim()[0] ?? '·').toUpperCase()}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate font-semibold text-foreground">{pickerLabel}</span>
            {pickerMeta ? <span className="block truncate text-xs text-muted-foreground">{pickerMeta}</span> : null}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        </button>
        {pickerOpen ? (
          <div
            className="absolute left-0 top-full z-30 mt-2 flex max-h-[70vh] w-[min(28rem,calc(100vw-3rem))] flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
            data-testid="fb-course-menu"
          >
            <div className="p-3 pb-2">
              <Input
                autoFocus
                value={courseSearch}
                onChange={(event) => setCourseSearch(event.target.value)}
                placeholder={`Find a course (${catalog?.courses.length ?? 0})`}
                aria-label="Find a course"
              />
            </div>
            <div className="min-h-0 overflow-y-auto px-2 pb-3">
              {assigned.length ? (
                <>
                  <GroupLabel label="Assigned to learners" count={assigned.length} />
                  {assigned.map((course) => (
                    <CourseRow key={course.id} course={course} active={course.id === courseId} onPick={() => pickCourse(course.id)} />
                  ))}
                </>
              ) : null}
              {other.length ? (
                <>
                  <GroupLabel label="Other courses" count={other.length} />
                  {other.map((course) => (
                    <CourseRow key={course.id} course={course} active={course.id === courseId} onPick={() => pickCourse(course.id)} />
                  ))}
                </>
              ) : null}
              {unassignedMatches ? (
                <button
                  type="button"
                  onClick={() => pickCourse(UNASSIGNED_COURSE)}
                  data-testid="fb-course-option-unassigned"
                  className={cn(
                    'mt-3 flex w-full items-center gap-3 rounded-xl px-2.5 py-1.5 text-left text-sm hover:bg-foundation-calm-blue-50',
                    courseId === UNASSIGNED_COURSE && 'bg-foundation-calm-blue-50 font-semibold',
                  )}
                >
                  <span aria-hidden="true" className="h-2.5 w-2.5 shrink-0 rounded-[3px] bg-foundation-base-darkbase-dark-300" />
                  <span className="min-w-0 flex-1 truncate">Modules not in any course</span>
                  <span className="shrink-0 text-xs text-muted-foreground">{unassignedCount}</span>
                </button>
              ) : null}
              {grouped.tests.length > 0 && (tests.length > 0 || !term) ? (
                <>
                  <button
                    type="button"
                    onClick={() => setShowTests((open) => !open)}
                    aria-expanded={showTests || !!term}
                    data-testid="fb-course-tests-toggle"
                    className="mt-3 flex w-full items-center gap-1.5 px-2.5 pb-1 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
                  >
                    {showTests || term ? <ChevronDown className="h-3 w-3" aria-hidden="true" /> : <ChevronRight className="h-3 w-3" aria-hidden="true" />}
                    Looks like a test or old copy <span className="font-normal">{tests.length}</span>
                  </button>
                  {showTests || term
                    ? tests.map((course) => (
                        <CourseRow key={course.id} course={course} active={course.id === courseId} onPick={() => pickCourse(course.id)} />
                      ))
                    : null}
                </>
              ) : null}
              {term && !assigned.length && !other.length && !tests.length && !unassignedMatches ? (
                <p className="px-2.5 py-3 text-sm text-muted-foreground">No courses match.</p>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      {typeof courseId === 'number' ? <CourseJumpLinks courseId={courseId} current="module-builder" className="px-2 pt-2" /> : null}
      <div className="mt-2 min-h-[12rem] flex-1 overflow-y-auto pr-1" data-testid="fb-module-outline">
        {catalogError ? <p className="p-3 text-sm text-destructive">{catalogError}</p> : null}
        {treeError ? <p role="alert" className="p-3 text-sm text-destructive">{treeError}</p> : null}
        {treeLoading && !visibleTree ? (
          <div className="flex justify-center py-10"><LoadingAnimation /></div>
        ) : null}
        {visibleTree && visibleTree.modules.length === 0 ? (
          <p className="p-3 text-sm text-muted-foreground">
            {courseId === UNASSIGNED_COURSE ? 'Every module is in a course.' : 'This course has no modules yet.'}
          </p>
        ) : null}
        {groups.map((group) => (
          <React.Fragment key={`${group.key}-${group.modules[0]?.number}`}>
            {group.label ? (
              <p className="flex items-center px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                <span className="min-w-0 flex-1 truncate">{group.label}</span>
                <span className="font-normal">{group.modules.length}</span>
              </p>
            ) : null}
            {group.modules.map(({ module, number }) => {
              const { name, version } = moduleDisplayName(module, visibleTree?.course?.name);
              const moduleFlags = flags.get(module.id);
              const isRoleplay =
                moduleFlags?.is_roleplay ?? module.questions.some((question) => question.type === 'roleplay');
              const isSelected = module.id === selectedFlowId;
              return (
                <button
                  key={module.id}
                  type="button"
                  onClick={() => onSelectModule(module.id)}
                  aria-current={isSelected ? 'true' : undefined}
                  title={module.name}
                  data-testid={`fb-module-${module.id}`}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-xl px-2 py-1.5 text-left text-sm hover:bg-white/60',
                    isSelected ? 'bg-foundation-orange-50 font-semibold text-foreground' : 'text-foundation-base-darkbase-dark-500',
                  )}
                >
                  <span className="grid h-5 w-5 shrink-0 place-items-center rounded-md bg-foundation-base-darkbase-dark-500/5 text-[11px] font-medium text-muted-foreground">
                    {number}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{name}</span>
                  {module.is_active === false ? (
                    <span className="shrink-0 rounded-full bg-amber-100 px-1.5 text-[10.5px] font-medium text-amber-800">Inactive</span>
                  ) : null}
                  {version ? (
                    <span className="shrink-0 rounded-full bg-foundation-base-darkbase-dark-500/5 px-1.5 text-[10.5px] font-normal text-muted-foreground">
                      {version}
                    </span>
                  ) : null}
                  <span className="flex shrink-0 items-center gap-1 text-muted-foreground">
                    {moduleFlags?.is_quiz ? <ClipboardCheck className="h-3.5 w-3.5" aria-label="Quiz" /> : null}
                    {isRoleplay ? <MessagesSquare className="h-3.5 w-3.5" aria-label="Roleplay" /> : null}
                    {moduleFlags?.voice_mode_enabled ? <Mic className="h-3.5 w-3.5" aria-label="Voice Mode" /> : null}
                  </span>
                  <span className="w-5 shrink-0 text-right text-xs font-normal text-muted-foreground">{module.question_count}</span>
                </button>
              );
            })}
          </React.Fragment>
        ))}
      </div>
      {activeCourse?.looks_like_test ? (
        <p className="px-2 pt-2 text-xs text-muted-foreground">This course looks like a test or old copy.</p>
      ) : null}
      <Button type="button" variant="secondary" size="auto" className="mt-2 w-full px-4 py-2" onClick={onCreateModule}>
        <Plus className="h-4 w-4" aria-hidden="true" /> New module
      </Button>
    </div>
  );
};

export default ModuleCourseBrowser;
